/**
 * The statuses a request is sent again on, by method. A 500 never is: the
 * backend answers a failure that passes with 503 (or 502/504 for another
 * service it could not reach) and a 500 means a bug, which shows at once.
 *
 * - Idempotent methods (GET, HEAD, OPTIONS, PUT, DELETE) are retried on
 *   every transient status: sending them twice does no harm.
 * - Every other method (POST, PATCH, …) only on the statuses that say the
 *   request was not carried out — timed out before it was read (408), too
 *   early (425), rate-limited (429), not served (503). A 502 or 504 may come
 *   after the work was done, and a second POST would do it twice.
 */
const IDEMPOTENT_RETRY_STATUSES: readonly number[] = [408, 425, 429, 502, 503, 504];
const NOT_PROCESSED_RETRY_STATUSES: readonly number[] = [408, 425, 429, 503];
const IDEMPOTENT_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE']);
const DEFAULT_MAX_RETRIES = 4;
const DEFAULT_INITIAL_DELAY_MS = 500;
const DEFAULT_MAX_DELAY_MS = 16_000;
const DEFAULT_JITTER_MS = 250;

export interface BackoffOptions {
  maxRetries?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  jitterMs?: number;
  /** Replaces the method's default retry statuses (see defaultRetryStatuses). */
  retryStatuses?: readonly number[];
  /** Retry an ambiguous network failure. Defaults to idempotent HTTP methods only.
   * Known read-only POST envelopes may opt in; mutations must not be replayed. */
  retryNetworkErrors?: boolean;
  /** The request's method (default GET, as fetch's); picks the default retry statuses. */
  method?: string;
  signal?: AbortSignal;
  onRetry?: (info: { attempt: number; delayMs: number; status?: number; error?: unknown }) => void;
}

/**
 * The statuses a request with this method is retried on when the caller
 * names none: every transient status for an idempotent method, only the
 * "not processed" ones for any other. Never 500.
 */
export function defaultRetryStatuses(method: string | undefined): readonly number[] {
  const normalized = (method || 'GET').toUpperCase();
  return IDEMPOTENT_METHODS.has(normalized) ? IDEMPOTENT_RETRY_STATUSES : NOT_PROCESSED_RETRY_STATUSES;
}

interface NormalizedOptions {
  maxRetries: number;
  initialDelayMs: number;
  maxDelayMs: number;
  jitterMs: number;
  retryStatuses: readonly number[];
  retryNetworkErrors: boolean;
  signal: AbortSignal | undefined;
  onRetry: BackoffOptions['onRetry'];
}

function normalizeOptions(options: BackoffOptions | undefined): NormalizedOptions {
  return {
    maxRetries: clampNonNegative(options?.maxRetries, DEFAULT_MAX_RETRIES),
    initialDelayMs: clampPositive(options?.initialDelayMs, DEFAULT_INITIAL_DELAY_MS),
    maxDelayMs: clampPositive(options?.maxDelayMs, DEFAULT_MAX_DELAY_MS),
    jitterMs: Math.max(0, Number(options?.jitterMs ?? DEFAULT_JITTER_MS) || 0),
    retryStatuses: options?.retryStatuses?.length ? options.retryStatuses : defaultRetryStatuses(options?.method),
    retryNetworkErrors: options?.retryNetworkErrors ?? IDEMPOTENT_METHODS.has((options?.method || 'GET').toUpperCase()),
    signal: options?.signal,
    onRetry: options?.onRetry,
  };
}

function clampNonNegative(value: number | undefined, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

function clampPositive(value: number | undefined, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Parses a Retry-After header. Returns ms to wait, or null if absent/unparseable.
 * Supports both delta-seconds form (`Retry-After: 30`) and HTTP-date form.
 */
export function parseRetryAfter(headerValue: string | null | undefined, nowMs: number = Date.now()): number | null {
  const raw = String(headerValue || '').trim();
  if (!raw) return null;

  if (/^\d+$/.test(raw)) {
    const seconds = Number(raw);
    return Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : null;
  }

  const httpDate = Date.parse(raw);
  if (!Number.isFinite(httpDate)) return null;
  return Math.max(0, httpDate - nowMs);
}

/**
 * Exponential backoff with full jitter (AWS architecture blog recipe).
 * Capped at maxDelayMs.
 *
 *   delay = min(maxDelayMs, initialDelayMs * 2^attempt) + random(0, jitterMs)
 *
 * If a Retry-After header is present, that value wins (but is still capped).
 */
export function calculateBackoffDelayMs(
  attempt: number,
  retryAfterMs: number | null,
  options: BackoffOptions = {},
): number {
  const { initialDelayMs, maxDelayMs, jitterMs } = normalizeOptions(options);
  if (retryAfterMs !== null && retryAfterMs >= 0) {
    return Math.min(maxDelayMs, retryAfterMs);
  }
  const exponential = Math.min(maxDelayMs, initialDelayMs * 2 ** Math.max(0, attempt));
  const jitter = jitterMs > 0 ? Math.random() * jitterMs : 0;
  return Math.min(maxDelayMs, exponential + jitter);
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function isRetryableNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  // Aborts are user-initiated cancellations — never retry.
  const name = (error as { name?: string }).name;
  if (name === 'AbortError') return false;
  // TypeError is what fetch throws for network failures (DNS, offline, CORS at L4).
  if (error instanceof TypeError) return true;
  // Anything else (HttpErrors, business errors) propagates.
  return false;
}

/**
 * Wraps a fetch call with exponential backoff for the transient statuses of
 * its method (defaultRetryStatuses: pass options.method for anything but a
 * GET). Network failures may have lost a successful response, so they are
 * retried only for idempotent methods or an explicit read-only opt-in.
 * Honors Retry-After. Stops after
 * maxRetries attempts.
 *
 * Returns the final Response (the last attempt — successful, or the last
 * retryable-status response if we exhaust retries) or throws the last error
 * if every attempt threw.
 */
export async function fetchWithBackoff(
  doFetch: () => Promise<Response>,
  options?: BackoffOptions,
): Promise<Response> {
  const normalized = normalizeOptions(options);
  let lastResponse: Response | null = null;
  let lastError: unknown = null;

  for (let attempt = 0; attempt <= normalized.maxRetries; attempt += 1) {
    if (normalized.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    try {
      const response = await doFetch();
      if (!normalized.retryStatuses.includes(response.status)) {
        return response;
      }
      lastResponse = response;

      if (attempt === normalized.maxRetries) {
        return response;
      }

      const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
      const delayMs = calculateBackoffDelayMs(attempt, retryAfter, options);
      normalized.onRetry?.({ attempt: attempt + 1, delayMs, status: response.status });
      await sleep(delayMs, normalized.signal);
    } catch (error) {
      lastError = error;
      if (!normalized.retryNetworkErrors || !isRetryableNetworkError(error) || attempt === normalized.maxRetries) {
        throw error;
      }
      const delayMs = calculateBackoffDelayMs(attempt, null, options);
      normalized.onRetry?.({ attempt: attempt + 1, delayMs, error });
      await sleep(delayMs, normalized.signal);
    }
  }

  if (lastResponse) return lastResponse;
  throw lastError ?? new Error('fetchWithBackoff: no attempts executed');
}
